import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { SwapFreeOptionDto } from 'api/Clients/Types/SwapFreeOptionDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { GridLoading } from 'components/GridLoading'
import MoreButton from 'components/MoreButton/MoreButton'
import {
  createGridState,
  GridStateStatus,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { AddEditSwapFreeOptionForm } from './AddEditSwapFreeOptionForm'
import {
  SwapFreeOptionsTableFilter,
  useSwapFreeOptionsBuildQuery,
} from './SwapFreeOptionsTableFilter'
import { swapFreeRatingColumns } from './swapFreeRatingColumns'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchCountriesAction: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTickmillCompaniesAction: async () =>
      dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

interface SwapFreeOptionFetchProps {
  setSwapFreeOptions: Dispatch<SetStateAction<SwapFreeOptionDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
}

const useSwapFreeOptionsFetch = ({
  setSwapFreeOptions: setSwapfreeOptions,
  tableData,
  setTableData,
}: SwapFreeOptionFetchProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { gridState, gridActions } = useGridState()
  const { filters } = useMapStateToProps()
  const { fetchFilters } = useMapDispatchToProps()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const { getQuery } = useSwapFreeOptionsBuildQuery()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<SwapFreeOptionDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const response = await tradingAccountsApiClient.getSwapFreeOptions({
            search: searchQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setSwapfreeOptions(response.items)
          }
          setTableData({ ...tableData, meta: response })

          if (isEmpty(filters)) await fetchFilters()

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<SwapFreeOptionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }
  return {
    isLoading: gridStatusState.isLoading,
    gridStatusState,
    gridStatusActions,
  }
}

interface SwapFreeOptionsPageableProps {
  setSwapFreeOptions: Dispatch<SetStateAction<SwapFreeOptionDto[]>>
  tableData: TableStateContextData
  setTableData: Dispatch<SetStateAction<TableStateContextData>>
  gridStatusState: GridStateStatus
}

const useSwapFreeOptionsPageable = ({
  setSwapFreeOptions,
  tableData,
  setTableData,
  gridStatusState,
}: SwapFreeOptionsPageableProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { gridActions } = useGridState()
  const { getQuery } = useSwapFreeOptionsBuildQuery()
  const gridStateSelector = useAppSelector((state) => state.grid)

  const handleSkipCountChange = async (skipCount: boolean) => {
    refreshGrid({
      skipCount,
    })
  }

  const handlePageChange = async (page: number) => {
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    refreshGrid({ pageNumber: pageIndex })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      pageSize,
    })

    refreshGrid({ pageSize, pageNumber: 1 })
  }

  const refreshGrid = async (params?: {
    pageSize?: number
    pageNumber?: number
    skipCount?: boolean
  }) => {
    const searchQuery = getQuery()
    const skipCount = params?.skipCount ?? tableData?.skipCount !== false

    gridStatusState.gridStatusActions.setFetching(true)
    const response = await tradingAccountsApiClient.getSwapFreeOptions({
      search: searchQuery,
      pageIndex: params?.pageNumber ?? gridStateSelector.pageIndex,
      pageSize: params?.pageSize ?? gridStateSelector.pageSize,
      skipCount,
    })
    gridStatusState.gridStatusActions.setFetching(false)

    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setSwapFreeOptions(response.items)
    }
    setTableData({ ...tableData, skipCount, meta: response })
  }

  return { handlePageChange, handlePageSizeChange, handleSkipCountChange, refreshGrid }
}

const SwapFreeRating = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [swapFreeOptions, setSwapFreeOptions] = useState<SwapFreeOptionDto[]>([])
  const [isAddEditFormOpen, setIsAddEditFormOpen] = useState<{
    visible: boolean
    selectedOption?: SwapFreeOptionDto | undefined
  }>({ visible: false })

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const countries = useAppSelector((state) => state.identity.countries)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const { fetchCountriesAction, fetchTickmillCompaniesAction } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(countries)) {
      fetchCountriesAction()
    }
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompaniesAction()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { isLoading, ...gridStatusState } = useSwapFreeOptionsFetch({
    setSwapFreeOptions,
    tableData,
    setTableData,
  })

  const { handlePageChange, handlePageSizeChange, handleSkipCountChange, refreshGrid } =
    useSwapFreeOptionsPageable({
      setSwapFreeOptions,
      tableData,
      setTableData,
      gridStatusState,
    })

  const { gridState } = useGridState()

  const handleFilter = (data: SwapFreeOptionDto[]) => {
    setSwapFreeOptions(data)
  }

  const handleAddCountry = () => {
    setIsAddEditFormOpen({ visible: true, selectedOption: undefined })
  }

  const handleEditCountry = (option: SwapFreeOptionDto) => {
    setIsAddEditFormOpen({ visible: true, selectedOption: option })
  }

  return (
    <>
      {isAddEditFormOpen.visible && (
        <AddEditSwapFreeOptionForm
          key={isAddEditFormOpen.selectedOption?.id ?? 'new'}
          tickmillCompanies={tickmillCompanies}
          isOpen={isAddEditFormOpen.visible}
          onClose={() =>
            setIsAddEditFormOpen({
              visible: false,
              selectedOption: isAddEditFormOpen.selectedOption,
            })
          }
          countries={countries}
          swapFreeOption={isAddEditFormOpen.selectedOption}
          onSubmit={async (values) => {
            if (isAddEditFormOpen.selectedOption) {
              await tradingAccountsApiClient.updateSwapFreeOption(values)
            } else {
              await tradingAccountsApiClient.createSwapFreeOption(values)
            }
            refreshGrid()

            setIsAddEditFormOpen({
              visible: false,
              selectedOption: isAddEditFormOpen.selectedOption,
            })
          }}
        />
      )}
      <Breadcrumbs
        crumbs={[{ title: 'Swap Free Rating', link: links.brokerageSwapFreeRating.url }]}
      />
      <SwapFreeOptionsTableFilter
        tableData={tableData}
        setTableData={setTableData}
        gridStatusState={gridStatusState}
        onChange={handleFilter}
      />
      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanAddSwapFreeOption)}
        text='Add countries'
        onClick={handleAddCountry}
      />
      <GridLoading isLoading={gridStatusState.gridStatusState.isLoading}>
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            resizable
            data={swapFreeOptions}
            skip={gridState.skip}
            take={gridState.take}
            total={gridState.total}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take}
                skip={gridState.skip}
              />
            )}
          >
            {swapFreeRatingColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={50}
              cell={({ dataItem }) => {
                const rowData = dataItem as SwapFreeOptionDto
                return (
                  <td>
                    <MoreButton
                      items={[
                        {
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanEditSwapFreeOption
                          ),
                          name: 'Edit',
                          onClick: () => {
                            handleEditCountry(rowData)
                          },
                        },
                      ]}
                    />
                  </td>
                )
              }}
            />
          </Grid>
        </TableStateContext.Provider>
      </GridLoading>
    </>
  )
}

export default SwapFreeRating
